import { Injectable } from '@angular/core';
import { loadStripe, type StripeEmbeddedCheckout } from '@stripe/stripe-js';
import { environment } from '../../../environments/environment';
import { logCritical } from '../logging/logger.service';

export type StripeEmbeddedCheckoutInitResult =
  | { success: true }
  | { success: false; message: string };

@Injectable({ providedIn: 'root' })
export class StripeCheckoutService {
  private checkoutInstance: StripeEmbeddedCheckout | null = null;

  /**
   * Tears down any embedded checkout previously mounted by this service.
   */
  destroy(): void {
    if (this.checkoutInstance) {
      try {
        this.checkoutInstance.destroy();
      } catch {
        /* Stripe may throw if DOM is gone */
      }
      this.checkoutInstance = null;
    }
  }

  /**
   * Loads Stripe.js, initializes embedded checkout for a fixed client secret, and mounts it.
   * Any prior instance from this service is destroyed first.
   */
  async initEmbeddedCheckout(
    clientSecret: string,
    elementSelector: string,
  ): Promise<StripeEmbeddedCheckoutInitResult> {
    this.destroy();

    const secret = clientSecret?.trim();
    if (!secret) {
      return { success: false, message: 'Missing client secret.' };
    }

    try {
      const stripe = await loadStripe(environment.stripePublishableKey);
      if (!stripe) {
        return { success: false, message: 'Failed to load Stripe.' };
      }

      const checkout = await stripe.initEmbeddedCheckout({
        fetchClientSecret: async () => secret,
      });

      const el = document.querySelector(elementSelector);
      if (!el) {
        checkout.destroy();
        return { success: false, message: `Payment mount element not found: ${elementSelector}` };
      }

      checkout.mount(elementSelector);
      this.checkoutInstance = checkout;
      return { success: true };
    } catch (err) {
      logCritical('core/services/stripe-checkout.service', 'Stripe initialization failed', err);
      return { success: false, message: 'Unexpected error during Stripe initialization.' };
    }
  }
}
